import React, { useEffect, useState } from 'react';
import { Shuffle, Loader2, X, PlayCircle, Dumbbell, ShieldAlert, TrendingDown } from 'lucide-react';
import { api } from '../../lib/api';
import { youtubeVideoUrl } from '../../lib/exerciseVideos';

const REASONS = [
  { id: 'equipment', label: 'No equipment',  icon: Dumbbell,    desc: 'Use what you have' },
  { id: 'injury',    label: 'Injury / pain', icon: ShieldAlert, desc: 'Safer variant' },
  { id: 'plateau',   label: 'Plateau / bored', icon: TrendingDown, desc: 'Different stimulus' },
];

/**
 * Bottom sheet that finds alternates for an exercise by name.
 * - Looks up the exercise in the library (`/api/exercises?search=...`)
 * - Calls `/api/exercises/{id}/alternates?reason=...`
 * - Renders up to 5 swaps with Demo links + a Pick button.
 *
 * Props:
 *   exerciseName : string
 *   userEquipment, userInjuries : arrays from the user's plan (for filtering)
 *   onPick : (alt) => void   — caller decides what to do with the chosen alt
 *   onClose : () => void
 */
export default function SwapSheet({ exerciseName, userEquipment = [], userInjuries = [], onPick, onClose }) {
  const [reason, setReason] = useState('equipment');
  const [loading, setLoading] = useState(false);
  const [alternates, setAlternates] = useState([]);
  const [resolvedId, setResolvedId] = useState(null);
  const [resolvedName, setResolvedName] = useState(exerciseName);
  const [err, setErr] = useState(null);

  // Resolve exercise name → id (best library match, fuzzy)
  useEffect(() => {
    setLoading(true); setErr(null);
    (async () => {
      try {
        const match = await api.exerciseMatch(exerciseName);
        setResolvedId(match.id);
        setResolvedName(match.name);
      } catch (e) {
        setErr(`"${exerciseName}" isn't in the library — can't suggest alternates.`);
        setAlternates([]);
      } finally { setLoading(false); }
    })();
  }, [exerciseName]);

  // Fetch alternates whenever reason or resolvedId changes.
  // We ALWAYS pass injuries (so they act as a hard filter) and equipment
  // (so they act as a soft ranking bias) regardless of the chosen reason —
  // a "no equipment" swap should still never recommend something that
  // aggravates a reported injury.
  useEffect(() => {
    if (!resolvedId) return;
    setLoading(true); setErr(null);
    const params = { reason };
    if (userEquipment.length) params.equipment = userEquipment.join(',');
    if (userInjuries.length) params.avoid_injuries = userInjuries.join(',');
    api.exerciseAlternates(resolvedId, params)
      .then(setAlternates)
      .catch((e) => setErr(e?.message || 'Failed to fetch alternates'))
      .finally(() => setLoading(false));
  }, [resolvedId, reason, userEquipment, userInjuries]);

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/75 flex items-end sm:items-center justify-center px-2 sm:px-4"
      onClick={onClose}
      data-testid="swap-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-t-2xl sm:rounded-2xl p-5 text-white max-h-[85vh] overflow-y-auto"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1rem)' }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#BEFF38] font-black flex items-center gap-1.5">
              <Shuffle className="w-3 h-3" /> Swap exercise
            </div>
            <h3 className="mt-1 text-lg font-black uppercase tracking-tight truncate">{resolvedName}</h3>
          </div>
          <button onClick={onClose} data-testid="swap-sheet-close" className="h-8 w-8 grid place-items-center rounded-full hover:bg-zinc-800">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Reason chooser */}
        <div className="mt-4 grid grid-cols-3 gap-1.5">
          {REASONS.map((r) => {
            const Icon = r.icon;
            const isActive = reason === r.id;
            return (
              <button
                key={r.id}
                onClick={() => setReason(r.id)}
                data-testid={`swap-reason-${r.id}`}
                className={`flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg border transition-colors ${
                  isActive
                    ? 'border-[#BEFF38] bg-[#BEFF38]/10'
                    : 'border-zinc-800 hover:border-zinc-600'
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-[#BEFF38]' : 'text-zinc-400'}`} />
                <span className={`text-[10px] font-black uppercase tracking-wider text-center ${isActive ? 'text-white' : 'text-zinc-300'}`}>{r.label}</span>
              </button>
            );
          })}
        </div>
        <p className="mt-1.5 text-[11px] text-zinc-500 text-center">
          {REASONS.find((r) => r.id === reason)?.desc}
        </p>

        {/* Always-on safety summary — makes it obvious that injuries/equipment
            are factored in regardless of which reason is selected. */}
        {(userInjuries.length > 0 || userEquipment.length > 0) && (
          <div
            data-testid="swap-safety-banner"
            className="mt-3 px-3 py-2 rounded-lg bg-zinc-800/50 border border-zinc-800 text-[10px] leading-relaxed text-zinc-400"
          >
            {userInjuries.length > 0 && (
              <div data-testid="swap-honouring-injuries">
                <span className="text-rose-300 font-bold">Avoiding</span>{' '}
                {userInjuries.join(' · ')}
              </div>
            )}
            {userEquipment.length > 0 && (
              <div data-testid="swap-preferring-equipment" className={userInjuries.length > 0 ? 'mt-0.5' : ''}>
                <span className="text-[#BEFF38] font-bold">Preferring</span>{' '}
                {userEquipment.slice(0, 4).join(' · ')}{userEquipment.length > 4 ? '…' : ''}
              </div>
            )}
          </div>
        )}

        {/* Body */}
        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="py-6 flex items-center gap-2 text-zinc-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Finding swaps…
            </div>
          ) : err ? (
            <p className="text-xs text-zinc-500 italic py-2">{err}</p>
          ) : alternates.length === 0 ? (
            <p className="text-xs text-zinc-500 italic py-2">No suitable alternates found for this filter.</p>
          ) : (
            alternates.map((alt) => {
              const altEquip = alt.equipment || [];
              const ownsEquip = userEquipment.length > 0 && altEquip.some((e) => userEquipment.includes(e));
              return (
              <div
                key={alt.id}
                data-testid={`swap-alt-${alt.id}`}
                className="rounded-lg border border-zinc-800 bg-[#0b0c10] p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-white font-bold text-sm truncate">{alt.name}</p>
                      {ownsEquip && (
                        <span
                          data-testid={`swap-alt-owned-${alt.id}`}
                          className="text-[8px] font-black uppercase tracking-wider text-black bg-[#BEFF38] px-1.5 py-0.5 rounded"
                        >
                          Have it
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-0.5">
                      {alt.category} · {(alt.muscles || []).slice(0, 2).join(', ')}
                    </p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">{altEquip.join(' · ')}</p>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <a
                    href={youtubeVideoUrl(alt.name)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`swap-alt-demo-${alt.id}`}
                    className="inline-flex items-center gap-1 text-[10px] font-black text-[#ff7a1a] uppercase tracking-wider"
                  >
                    <PlayCircle className="w-3.5 h-3.5" /> Demo
                  </a>
                  <button
                    onClick={() => { onPick && onPick(alt); onClose && onClose(); }}
                    data-testid={`swap-alt-pick-${alt.id}`}
                    className="ml-auto inline-flex items-center gap-1 px-3 h-8 rounded-full bg-[#BEFF38] text-black text-[10px] font-black uppercase tracking-wider hover:bg-[#a6e62e]"
                  >
                    Use this
                  </button>
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
